import React, { createContext, useState, useEffect, useContext } from "react";
import Loading from "../loading";
import { getCurrentUser, logout, login } from "../../services/auth.service";
import { getUserProfileImageURL } from "../../Utils";
import { useLocation } from "react-router-dom";
// Context for Authentication
const AuthContext = createContext(undefined);

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const location = useLocation();
  // Function to handle user login
  const handleLogin = async (email, password) => {
    setLoading(true);
    try {
      const res = await login(email, password);

      if (res.success) {
        const connectedUser = {
          ...res.data,
          image: getUserProfileImageURL(res?.data?.userId),
        };

        setCurrentUser(connectedUser);
        localStorage.setItem("userLoginRequestID", res?.data?.userId); // Store in localStorage for persistence
      }
      return res;
    } catch (error) {
      console.error("Login error", error);
      return { success: false, msg: "Login failed, please try again." };
    } finally {
      setLoading(false);
    }
  };

  // Function to handle user logout
  const handelLogout = async () => {
    if (!currentUser?.userId) return;

    setLoading(true);
    try {
      const res = await logout(currentUser.userId);
      if (res) {
        setCurrentUser(null);
        localStorage.removeItem("userLoginRequestID"); // Remove user from localStorage
      } else {
        console.error("Logout API error");
      }
    } catch (error) {
      console.error("Logout error", error);
    } finally {
      setLoading(false);
    }
  };

  // Update user profile information
  const updateCurrentUser = async (userInfo) => {
    setCurrentUser((prevState) => ({
      ...prevState,
      ...userInfo,
    }));
  };

  // Fetch current user data on component mount or after login
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        let token = localStorage.getItem("accessToken");
        let refreshToken = localStorage.getItem("refreshToken");

        if (!token) {
          const params = new URLSearchParams(location.search);
          token = params.get("token");
          refreshToken = params.get("refreshToken");
          if (token) localStorage.setItem("accessToken", token);
          if (refreshToken) localStorage.setItem("refreshToken", refreshToken);
        }

        const user = await getCurrentUser(token, refreshToken);

        const userWithImage = {
          ...user,
          image: getUserProfileImageURL(user.userId),
        };

        setCurrentUser(userWithImage);
      } catch (err) {
        console.error("Erreur récupération utilisateur :", err);
        setCurrentUser(null);
      } finally {
        setLoading(false);
      }
    };

    fetchCurrentUser();
  }, [location.search]);

  // If the app is loading, show the loading component
  if (loading) {
    return <Loading />;
  }

  return (
    <AuthContext.Provider
      value={{
        currentUser,
        handleLogin,
        handelLogout,
        updateCurrentUser,
      }}>
      {children}
    </AuthContext.Provider>
  );
}

// Custom hook to access the Auth context
export function useAuth() {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error("useAuth must be used inside of an AuthProvider");
  }

  return context;
}
